import './styles.scss';

import { Card, Collapse } from 'react-bootstrap';
import { useEffect, useState } from 'react';

import Chart from '@/common/Chart';
import { Datetime } from '@/utils/dates';
import { getDtrScatterPlot } from '@/features/DriveTimeTool/api/services/getDtrScatterPlot';
import { getVariantColor } from '@/utils/styles';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

const RED_DOT_THRESHOLD = 45; // minutes
const RED_DOT_COLOR = getVariantColor('red');
const BLUE_DOT_COLOR = getVariantColor('primary');

export type DriveTimeScatterPlotProps = {
  height?: string;
  title?: string;
};

const DriveTimeScatterPlot = ({ height, title }: DriveTimeScatterPlotProps): JSX.Element => {
  const [{ selected, selectedFrom, loading }, setState] = useDriveTimeTool(({ state, setState }) => [
    { selected: state.selected, selectedFrom: state.selectedFrom || 'queryHistoryId', loading: state.loading.scatterPlot },
    setState,
  ]);
  const [stats, setStats] = useState<[x: number, y: number][]>([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const onToggleExpand = () => setIsExpanded((current) => !current);

  const fetchData = async (id: string): Promise<void> => {
    try {
      if (loading) return;
      setState((state) => ({ ...state, loading: { ...state.loading, scatterPlot: true } }));
      const res = await getDtrScatterPlot({ [selectedFrom]: selected });
      setStats((res?.values || []).map((data) => [data.hourWithOffset, data.driveTimeMinutes]));
    } catch (e) {
      console.error('fetchData', e);
    } finally {
      setState((state) => ({ ...state, loading: { ...state.loading, scatterPlot: false } }));
    }
  };

  useEffect(() => {
    if (!selected) return setStats([]);
    fetchData(selected);
  }, [selected]);

  return (
    <div className="DriveTimeScatterPlot">
      <Card className="scatter-plot-card">
        <Card.Body>
          <div className="scatter-plot-header" onClick={onToggleExpand}>
            <div className="header-content">
              <div className="header-left">
                <div className="chart-icon">
                  <i className="sv sv-graph"></i>
                </div>
                <div className="header-text">
                  <h5 className="header-title">Drive Time Scatter Plot</h5>
                  <p className="header-subtitle">Visualize drive time distribution and patterns</p>
                </div>
              </div>
              <div className="collapse-icon">
                <i
                  className={`fas fs-4 ${loading ? 'fa fa-spinner fa-pulse opacity-50' : isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                />
              </div>
            </div>
          </div>

          <Collapse in={isExpanded}>
            <div className="scatter-plot-content">
              <div className="chart-section">
                {!stats.length && (
                  <div className="chart-placeholder">
                    <div className="chart-icon-large">
                      <i className="sv sv-graph"></i>
                    </div>
                    <h4 className="placeholder-title">Ready for Scatter Plot Analysis</h4>
                    <p className="placeholder-description">
                      Select queries from the history above to visualize drive time data as scatter plots.
                    </p>
                  </div>
                )}
                {stats.length > 0 && (
                  <>
                    <Chart
                      style={{ height: height || '52rem', width: '100%' }}
                      option={{
                        title: {
                          text: title || 'Drivetime Outliers',
                          left: 'center', // This centers the title horizontally
                          textStyle: {
                            fontWeight: 700,
                            fontSize: 12,
                          },
                        },
                        grid: {
                          top: 20, // Adjust this value to control the distance from the top of the chart container
                          left: '3%',
                          right: '3%',
                          containLabel: true, // Ensures labels are contained within the grid
                        },
                        xAxis: {
                          type: 'value',
                          name: 'Hours of Day (Starting at 3 AM)',
                          nameLocation: 'middle',
                          nameGap: 30,
                          nameTextStyle: {
                            fontWeight: 700,
                          },
                          min: 3,
                          max: 25,
                          interval: 2,
                          axisLabel: {
                            formatter: (value) => ZERO_DATETIME.clone().add(value % 24, 'hours').time,
                          },
                          splitLine: {
                            show: false, // Hides horizontal grid lines
                          },
                        },
                        yAxis: {
                          type: 'value', // For continuous numerical data
                          name: 'Drivetime (Minutes)',
                          nameLocation: 'middle',
                          nameGap: 30,
                          nameTextStyle: {
                            fontWeight: 700,
                          },
                          min: 0,
                          max: 120,
                          interval: 10,
                          splitLine: {
                            show: false, // Hides vertical grid lines
                          },
                        },
                        series: [
                          {
                            name: 'Data Points',
                            type: 'scatter',
                            data: stats,
                            itemStyle: {
                              // if the y value is greater than 8, set the color to red
                              color: (params) => (params.value[1] > RED_DOT_THRESHOLD ? RED_DOT_COLOR : BLUE_DOT_COLOR),
                            },
                          },
                        ],
                      }}
                    />
                  </>
                )}
              </div>
            </div>
          </Collapse>
        </Card.Body>
      </Card>
    </div>
  );
};

const ZERO_DATETIME = new Datetime('0001-01-01T00:00:00');

export default DriveTimeScatterPlot;
